import useIndexedDB from "@/lib/useIndexedDB";
import {
  PlusIcon,
  TrashIcon,
  UserPlusIcon,
  XCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  UsersIcon,
} from "@heroicons/react/24/solid";
import { format } from "date-fns";
import useIntersectionObserver from "@/hooks/useIntersectionObserver";
import {
  classNames,
  fuzzySearchMutipleWords,
  isStudent,
  sortAlphabetically,
  sortAlphabeticallyReversed,
  sortByNewest,
  sortByOldest,
} from "@/lib/util";
import mixpanel from "mixpanel-browser";
import { nanoid } from "nanoid";
import Link from "next/link";
import { useUpdateSchoolMutation } from "@/queries/autogenerate/graphql";
import {
  Dispatch,
  Fragment,
  SetStateAction,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import Dropzone from "react-dropzone";
import toast from "react-hot-toast";
import Select from "react-select";
import { compareTwoStrings } from "string-similarity";
import AddStudentToClass from "./AddStudentToClass";
import DialogComponent from "./Dialog";
import Error from "./ErrorMessage";
import { CurrentUser, UserContext } from "./Page";
import DropzoneContainer from "./styles/DropzoneContainer";
import Tooltip from "./styles/Tooltip";
import UndrawEducator from "./svg/UndrawEducator";
import TextEditor from "./TextEditor";
import { ResponseData } from "@/pages/api/ai-text-to-users";

interface Props {
  limit?: number;
  setStudentsIntersectionRatio: Dispatch<SetStateAction<number>>;
  users: NonNullable<CurrentUser["school"]>["users"];
}

const SettingsStudents: React.FC<Props> = ({
  limit,
  setStudentsIntersectionRatio,
  users,
}) => {
  const [search, setSearch] = useState("");

  const [orderBy, setOrderBy] = useIndexedDB("settingsStudentsOrderBy", {
    label: "Alphabetical",
    value: "name_ASC",
  });

  const sortFunctionMap = new Map<string, any>([
    ["name_ASC", sortAlphabetically],
    ["name_DESC", sortAlphabeticallyReversed],
    ["createdAt_ASC", sortByOldest],
    ["createdAt_DESC", sortByNewest],
  ]);

  const [joinDialog, setJoinDialog] = useState<string | null>(null);
  const open = (id: string) => setJoinDialog(id);
  const close = () => setJoinDialog(null);

  useIntersectionObserver("students", [], (entry) => {
    setStudentsIntersectionRatio(entry.intersectionRatio);
  });

  const checkbox = useRef<HTMLInputElement>(null);
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const [rawSelectedStudents, setSelectedStudents] = useState<string[]>([]);

  const students = users.filter(isStudent);
  const selectedStudents = rawSelectedStudents.filter((s) =>
    students.some((u) => u.id === s)
  );

  useLayoutEffect(() => {
    const isIndeterminate =
      selectedStudents.length > 0 && selectedStudents.length < students.length;
    setChecked(selectedStudents.length === students.length);
    setIndeterminate(isIndeterminate);
    if (checkbox.current) {
      checkbox.current.indeterminate = isIndeterminate;
    }
  }, [selectedStudents]);

  function toggleAll() {
    setSelectedStudents(
      checked || indeterminate ? [] : students.map((s) => s.id)
    );
    setChecked(!checked && !indeterminate);
    setIndeterminate(false);
  }

  const unsortedStudents = users.filter(isStudent);
  // .filter((_, i) => (limit ? i < limit : true));

  const searchedStudents = fuzzySearchMutipleWords(unsortedStudents, search, {
    keys: ["name", "email"],
  }).filter((_, i) => (limit ? i < limit : true));

  const sortedStudents = [...searchedStudents].sort(
    sortFunctionMap.get(orderBy.value)
  );

  // Show true friends when there are no students

  return (
    <UserContext.Consumer>
      {(user) => (
        <div id="students">
          <div className="px-4 min-lg:px-8 min-sm:px-6">
            <div className="flex items-center justify-between">
              <h2 className="flex items-center">
                <UsersIcon className="m-2 w-9" /> Students
              </h2>
              <div className="flex items-center gap-2">
                <div className="grid w-[200px] grid-cols-1">
                  <input
                    id="students-search"
                    name="students-search"
                    type="text"
                    placeholder="Search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pl-10 pr-3 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:pl-9 sm:text-sm/6"
                  />
                  <MagnifyingGlassIcon
                    aria-hidden="true"
                    className="pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center text-gray-400 sm:size-4"
                  />
                </div>
                <Select
                  value={orderBy}
                  options={[
                    { label: "Alphabetical", value: "name_ASC" },
                    {
                      label: "Alphabetical (Descending)",
                      value: "name_DESC",
                    },
                    { label: "Oldest", value: "createdAt_ASC" },
                    { label: "Newest", value: "createdAt_DESC" },
                  ]}
                  noOptionsMessage={() => "No Options"}
                  onChange={setOrderBy}
                  isSearchable={false}
                  isClearable={false}
                  className="w-full"
                  styles={{
                    container: (provided) => ({
                      ...provided,
                      display: "inline-block",
                      width: "200px",
                      margin: "5px",
                      zIndex: 30,
                    }),
                  }}
                />
                <AddNewStudent />
              </div>
            </div>
            {/* <div className="min-sm:flex min-sm:items-center">
              <div className="min-sm:flex-auto">
                <h1 className="text-base font-semibold leading-6 text-gray-900">
                  Users
                </h1>
                <p className="mt-2 text-sm text-gray-700">
                  A list of all the users in your account including their name,
                  title, email and role.
                </p>
              </div>
              <div className="mt-4 min-sm:ml-16 min-sm:mt-0 min-sm:flex-none">
                <button
                  type="button"
                  className="block rounded-md bg-blue-600 px-3 py-1.5 text-center text-sm font-semibold leading-6 text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                >
                  Add user
                </button>
              </div>
            </div> */}
            <div className="mt-8 flow-root">
              <div className="-mx-4 -my-2 overflow-x-auto min-lg:-mx-8 min-sm:-mx-6">
                <div className="inline-block min-w-full py-2 align-middle min-lg:px-8 min-sm:px-6">
                  <div className="relative">
                    {selectedStudents.length > 0 && (
                      <div className="absolute left-14 top-0 flex h-12 items-center space-x-3 min-sm:left-12">
                        {/* <button
                          type="button"
                          className="inline-flex items-center rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white"
                        >
                          Bulk edit
                        </button> */}
                        <RemoveStudents
                          user={user}
                          students={students.filter((s) =>
                            selectedStudents.includes(s.id)
                          )}
                        >
                          {(open, fetching) => (
                            <button
                              type="button"
                              className="inline-flex items-center rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white"
                              disabled={fetching}
                              onClick={open}
                            >
                              Delete all selected
                            </button>
                          )}
                        </RemoveStudents>
                      </div>
                    )}
                    {students.length ? (
                      <table className="min-w-full table-fixed divide-y divide-gray-300">
                        <thead>
                          <tr>
                            <th
                              scope="col"
                              className="relative px-7 min-sm:w-12 min-sm:px-6"
                            >
                              <input
                                type="checkbox"
                                className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                                ref={checkbox}
                                checked={checked}
                                onChange={toggleAll}
                              />
                            </th>
                            <th
                              scope="col"
                              className="min-w-[12rem] py-3.5 pr-3 text-left text-sm font-semibold text-gray-900"
                            >
                              Name
                            </th>
                            {/* <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Title
                      </th> */}
                            <th
                              scope="col"
                              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                            >
                              Signed Up
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                            >
                              Classes
                            </th>
                            <th
                              scope="col"
                              className="relative py-3.5 pl-3 pr-4 min-sm:pr-3"
                            >
                              <span className="sr-only">Edit</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {sortedStudents.map((student) => (
                            <Fragment key={student.id}>
                              <tr
                                key={student.email}
                                className={classNames(
                                  "group",
                                  selectedStudents.includes(student.id)
                                    ? "bg-gray-50"
                                    : ""
                                )}
                              >
                                <td
                                  rowSpan={student.classes.length || 1}
                                  className="relative px-7 min-sm:w-12 min-sm:px-6"
                                >
                                  {selectedStudents.includes(student.id) && (
                                    <div className="absolute inset-y-0 left-0 w-0.5 bg-blue-600" />
                                  )}
                                  <input
                                    type="checkbox"
                                    className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                                    value={student.email}
                                    checked={selectedStudents.includes(
                                      student.id
                                    )}
                                    onChange={(e) =>
                                      setSelectedStudents(
                                        e.target.checked
                                          ? [...selectedStudents, student.id]
                                          : selectedStudents.filter(
                                              (p) => p !== student.id
                                            )
                                      )
                                    }
                                  />
                                </td>
                                <td
                                  rowSpan={student.classes.length || 1}
                                  className={classNames(
                                    "whitespace-nowrap py-4 pr-3 text-sm font-medium",
                                    selectedStudents.includes(student.id)
                                      ? "text-blue-600"
                                      : "text-gray-900"
                                  )}
                                >
                                  <p>{student.name}</p>
                                  <p className="text-gray-400">
                                    {student.email}
                                  </p>
                                </td>
                                {/* <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {student.title}
                        </td> */}
                                <td
                                  rowSpan={student.classes.length || 1}
                                  className="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                >
                                  {format(new Date(student.createdAt), "PPpp")}
                                </td>
                                <td
                                  className={classNames(
                                    "whitespace-nowrap px-3 py-4 text-sm text-gray-500",
                                    student.classes.length > 1
                                      ? "border-x border-gray-200"
                                      : ""
                                  )}
                                >
                                  {student.classes.length ? (
                                    <StudentClass
                                      user={user}
                                      student={student}
                                      classe={student.classes[0]}
                                    />
                                  ) : (
                                    <span>No Classes</span>
                                  )}
                                </td>
                                <td
                                  rowSpan={student.classes.length || 1}
                                  className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium min-sm:pr-3"
                                >
                                  <div className="flex items-center justify-around">
                                    <Tooltip
                                      content={`Add ${student.name} to a Class`}
                                    >
                                      <button onClick={() => open(student.id)}>
                                        <PlusIcon className="icon icon-gray opacity-0 group-hover:opacity-100" />
                                      </button>
                                    </Tooltip>
                                    <DialogComponent
                                      isOpen={joinDialog === student.id}
                                      onClose={close}
                                      aria-label="Add Student to Class"
                                    >
                                      <UserContext.Consumer>
                                        {(user) => (
                                          <AddStudentToClass
                                            user={user}
                                            student={student}
                                            onCompleted={close}
                                          />
                                        )}
                                      </UserContext.Consumer>
                                      <button
                                        onClick={close}
                                        className="btn btn-warning"
                                      >
                                        Cancel
                                      </button>
                                    </DialogComponent>

                                    <RemoveStudents
                                      user={user}
                                      students={[student]}
                                    >
                                      {(open, fetching) => (
                                        <button
                                          className=""
                                          onClick={open}
                                          disabled={fetching}
                                        >
                                          {fetching ? (
                                            <div className="spinner"></div>
                                          ) : null}
                                          <TrashIcon className="icon icon-orange opacity-0 group-hover:opacity-100" />
                                        </button>
                                      )}
                                    </RemoveStudents>
                                  </div>
                                </td>
                              </tr>
                              {student.classes
                                .filter((c, i) => i != 0)
                                .map((c) => (
                                  <tr key={c.id}>
                                    <td className="whitespace-nowrap border-x border-gray-200 px-3 py-4 text-sm text-gray-500">
                                      <StudentClass
                                        user={user}
                                        student={student}
                                        classe={c}
                                      />
                                    </td>
                                  </tr>
                                ))}
                            </Fragment>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-18">
                        <UndrawEducator className="w-56" />
                        <div className="mt-10 text-center">
                          <h1 className="mb-2">No Student Accounts... Yet</h1>
                          {/* <p className="mb-8">Create recipes and begin managing....</p> */}
                          <AddNewStudent />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {limit && limit < users.filter(isStudent).length ? (
              <div className="flex items-center justify-center p-3">
                <Link prefetch={false} href="/students">
                  <button className="btn btn-subtle">See All Students</button>
                </Link>
              </div>
            ) : null}
          </div>
          {/* <div className="flex items-center justify-between">
        <h2 className="flex items-center">
          <UsersIcon className="m-2 w-9" /> Students
        </h2>
        <div className="flex items-center gap-2">
          <Select
            value={orderBy}
            options={[
              { label: "Alphabetical", value: "name_ASC" },
              {
                label: "Alphabetical (Descending)",
                value: "name_DESC",
              },
              { label: "Oldest", value: "createdAt_ASC" },
              { label: "Newest", value: "createdAt_DESC" },
            ]}
            noOptionsMessage={() => "No Options"}
            onChange={setOrderBy}
            isSearchable={false}
            isClearable={false}
            className="w-full"
            styles={{
              container: (provided) => ({
                ...provided,
                display: "inline-block",
                //   width: "150px",
                margin: "5px",
                zIndex: 30,
              }),
            }}
          />
          <AddNewStudent />
        </div>
      </div>
      <UserContext.Consumer>
        {(user) => (
          <div className="flex flex-col">
            <div className="-my-1.5 overflow-x-auto py-1.5 lg:-mx-5 lg:px-5 sm:-mx-3.5 sm:px-3.5">
              <div className="inline-block min-w-full overflow-hidden border-0 border-b border-solid border-gray-200 align-middle shadow sm:rounded-lg">
                <table className="min-w-full overflow-hidden rounded-lg bg-white shadow">
                  <thead>
                    <tr>
                      <th className="border-0 border-b border-solid border-gray-200 bg-gray-200 px-3.5 py-2.5 text-left  font-normal uppercase tracking-wider text-gray-500">
                        Name
                      </th>
                      <th className="border-0 border-b border-solid border-gray-200 bg-gray-200 px-3.5 py-2.5 text-left  font-normal uppercase tracking-wider text-gray-500">
                        Signed Up
                      </th>
                      <th className="border-0 border-b border-solid border-gray-200 bg-gray-200 px-3.5 py-2.5 text-left  font-normal uppercase tracking-wider text-gray-500">
                        Classes
                      </th>
                      <th className="border-0 border-b border-solid border-gray-200 bg-gray-200 px-3.5 py-2.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student) => (
                      <Fragment key={student.id}>
                        <tr className="group">
                          <td
                            rowSpan={student.classes.length || 1}
                            className="whitespace-no-wrap border-0 border-b border-solid border-gray-200 px-3.5 py-2.5 align-top"
                          >
                            <div className="flex items-center">
                              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-tr from-cyan-300 to-green-400 bg-cover text-2xl">
                                {student.name
                                  .split(" ")
                                  .map((n) => n.toUpperCase())
                                  .map((n) => n[0])
                                  .join("")}
                              </div>
                              <div className="ml-2.5">
                                <div className="font-normal text-gray-900">
                                  {student.name}
                                </div>
                                <div className="text-gray-500">
                                  {student.email}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td
                            rowSpan={student.classes.length || 1}
                            className="whitespace-no-wrap border-0 border-b border-solid border-gray-200 px-3.5 py-2.5 align-top"
                          >
                            <div className="text-gray-500">
                              {format(new Date(student.createdAt), "PPpp")}
                            </div>
                          </td>
                          {student.classes.length ? (
                            <td className="whitespace-no-wrap border-0 border-b border-l border-r border-solid border-gray-200 px-3.5 py-2.5">
                              <div className="group flex items-center justify-between">
                                <div>
                                  <div className="text-gray-900">
                                    {student.classes[0].name}
                                  </div>
                                  <div className="text-gray-500">
                                    {student.classes[0].users[0]?.name}
                                  </div>
                                </div>
                                <div className="px-3">
                                  <RemoveStudentFromClass
                                    {...{
                                      user,
                                      student,
                                      c: student.classes[0],
                                    }}
                                  />
                                </div>
                              </div>
                            </td>
                          ) : (
                            <td className="whitespace-no-wrap border-0 border-b border-solid border-gray-200 px-3.5 py-2.5">
                              <div className="text-gray-500">No Classes</div>
                            </td>
                          )}
                          <td
                            rowSpan={student.classes.length || 1}
                            className="whitespace-no-wrap border-0 border-b border-solid border-gray-200 px-3.5 py-2.5 text-right   font-normal"
                          >
                            <div className="flex items-center justify-around">
                              <Tooltip
                                content={`Add ${student.name} to a Class`}
                              >
                                <button onClick={() => open(student.id)}>
                                  <PlusIcon className="icon icon-gray opacity-0 group-hover:opacity-100" />
                                </button>
                              </Tooltip>
                              <DialogComponent
                                isOpen={joinDialog === student.id}
                                onClose={close}
                                aria-label="Add Student to Class"
                              >
                                <UserContext.Consumer>
                                  {(user) => (
                                    <AddStudentToClass
                                      user={user}
                                      student={student}
                                      onCompleted={close}
                                    />
                                  )}
                                </UserContext.Consumer>
                                <button
                                  onClick={close}
                                  className="btn btn-warning"
                                >
                                  Cancel
                                </button>
                              </DialogComponent>

                              <RemoveStudent student={student} />
                            </div>
                          </td>
                        </tr>
                        {student.classes
                          .filter((c, i) => i != 0)
                          .map((c) => (
                            <tr key={c.id}>
                              <td className="whitespace-no-wrap border-0 border-b border-l border-r border-solid border-gray-200 px-3.5 py-2.5">
                                <div className="group flex items-center justify-between">
                                  <div className="div">
                                    <div className="text-gray-900">
                                      {c.name}
                                    </div>
                                    <div className="text-gray-500">
                                      {c.users[0]?.name}
                                    </div>
                                  </div>
                                  <div className="px-3">
                                    <RemoveStudentFromClass
                                      {...{ user, student, c }}
                                    />
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </UserContext.Consumer>
      {limit && limit < users.filter(isStudent).length ? (
        <div className="flex items-center justify-center p-3">
          <Link prefetch={false} href="/students">
            <button className="btn btn-subtle">See All Students</button>
          </Link>
        </div>
      ) : null} */}
        </div>
      )}
    </UserContext.Consumer>
  );
};

interface StudentClassProps {
  user: CurrentUser;
  student: NonNullable<CurrentUser["school"]>["users"][number];
  classe: NonNullable<
    CurrentUser["school"]
  >["users"][number]["classes"][number];
}

const StudentClass: React.FC<StudentClassProps> = ({
  user,
  student,
  classe,
}) => {
  return (
    <div className="group flex items-center justify-between">
      <div>
        <div className="text-gray-900">{classe.name}</div>
        <div className="text-gray-500">{classe.users[0]?.name}</div>
      </div>
      <div className="px-3">
        <RemoveStudentFromClass
          {...{
            user,
            student,
            c: classe,
          }}
        />
      </div>
    </div>
  );
};

interface RemoveStudentsProps {
  user: CurrentUser;
  students: NonNullable<CurrentUser["school"]>["users"];
  children: (open: () => void, fetching: boolean) => React.ReactNode | null;
}

const RemoveStudents: React.FC<RemoveStudentsProps> = ({
  user,
  students,
  children,
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const open = () => setShowDialog(true);
  const close = () => setShowDialog(false);

  const [{ fetching, error }, updateSchool] = useUpdateSchoolMutation();

  if (error) return <Error error={error} />;

  return (
    <>
      <DialogComponent
        isOpen={showDialog}
        onClose={close}
        aria-label="Remove student"
      >
        <p>
          Are you sure you want to delete{" "}
          {students.length === 1
            ? students[0].name
            : `${students.length} student`}
          &#39;s account{students.length > 1 ? "s" : ""}? This action cannot be
          undone.
        </p>
        <button onClick={close} className="btn btn-warning">
          Cancel
        </button>
        <button
          className="btn btn-danger"
          onClick={() => {
            // deleteStudent({ id: student.id }).then(() => {
            //   toast.success("Student's account has been deleted.");

            //   mixpanel.track("Deleted a student account");
            //   close();
            // });

            updateSchool({
              where: { id: user.school?.id },
              data: {
                users: {
                  deleteMany: students.map((student) => ({
                    id: { equals: student.id },
                  })),
                },
              },
            }).then(() => {
              toast.success(
                `${
                  students.length === 1
                    ? students[0].name
                    : `${students.length} students`
                } account${
                  students.length > 1 ? "s have" : "has"
                } been deleted.`
              );

              mixpanel.track("Deleted a student account");
              close();
            });
          }}
          disabled={fetching}
        >
          {fetching ? <div className="spinner"></div> : null}
          Delete Account{students.length > 1 ? "s" : ""}
        </button>
      </DialogComponent>
      {children(open, fetching)}
    </>
  );
};

interface AddNewStudentProps {}

const AddNewStudent: React.FC<AddNewStudentProps> = () => {
  const [showDialog, setShowDialog] = useState(false);
  const open = () => setShowDialog(true);
  const close = () => setShowDialog(false);

  type StudentType = {
    id: string;
    name: string;
    email: string;
    index?: number;
  };

  const [students, setStudents] = useState<StudentType[]>([
    {
      id: nanoid(),
      name: "",
      email: "",
    },
  ]);

  const [ingestText, setIngestText] = useState(
    `<h2>Paste Here</h2>
<p>If you have user details in text format,</p>
<p>you can paste it here and ClassCart AI will parse it for you.</p>
    `
  );
  const [isParsing, setIsParsing] = useState(false);

  type RowType = {
    index: number;
    value: string;
  }[];
  const [headings, setHeadings] = useState<RowType>([]);
  const [rows, setRows] = useState<RowType[]>([]);

  const [emailHeading, setEmailHeading] = useState<number>(0);
  const [nameHeading, setNameHeading] = useState<number>(0);

  const [{ fetching, error }, updateSchool] = useUpdateSchoolMutation();

  const hasCSV =
    headings.length &&
    typeof emailHeading === "number" &&
    typeof nameHeading === "number";

  return (
    <>
      <button onClick={open} className="btn shrink-0">
        <UserPlusIcon className="w-5" /> Invite Students
      </button>
      <DialogComponent
        isOpen={showDialog}
        onClose={close}
        aria-label="Invite Users"
      >
        <h2 className="text-center font-medium">Invite Students</h2>
        <p className="mt-3 max-w-2xl">
          When you invite a student, their account will be created and connected
          to the school. They will then receive an email with their login
          details: email and password. We recommend that they change their
          password upon first login.
        </p>
        <p className="mt-3 max-w-2xl text-gray-600">
          If the students are not successfully invited, please confirm that they
          don&apos;t already have an account connected to their email address.
        </p>

        {/* CSV Upload Control Center */}
        {hasCSV ? (
          <div className="my-3 rounded bg-gray-200 p-2 shadow">
            <div className="mb-5 flex items-center justify-between">
              <h3 className="font-normal">CSV Mappings</h3>
              <button
                className="btn"
                onClick={() => {
                  setStudents([
                    {
                      id: nanoid(),
                      name: "",
                      email: "",
                    },
                  ]);

                  setHeadings([]);
                  setRows([]);

                  setEmailHeading(0);
                  setNameHeading(0);
                }}
              >
                Cancel CSV Upload
              </button>
            </div>
            <div className="my-1.5 flex items-center gap-2">
              Name maps to
              <Select
                name="name-mapping"
                value={headings
                  .map((heading) => ({
                    label: heading.value,
                    value: heading.index,
                  }))
                  .find((option) => option.value === nameHeading)}
                onChange={(heading) => {
                  setStudents(
                    students.map((s) => ({
                      ...s,
                      name:
                        typeof s.index === "number"
                          ? rows[s.index]?.find(
                              (item) => item.index === heading?.value
                            )?.value || "unknown"
                          : s.name || "unknown",
                    }))
                  );

                  setNameHeading(heading?.value || 0);
                }}
                options={headings.map((heading) => ({
                  label: heading.value,
                  value: heading.index,
                }))}
                noOptionsMessage={() => "No Options"}
                className="w-44"
                styles={{
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 20,
                  }),
                }}
              />
            </div>
            <div className="my-1.5 flex items-center gap-2">
              Email maps to
              <Select
                name="email-mapping"
                value={headings
                  .map((heading) => ({
                    label: heading.value,
                    value: heading.index,
                  }))
                  .find((option) => option.value === emailHeading)}
                onChange={(heading) => {
                  setStudents(
                    students.map((s) => ({
                      ...s,
                      email:
                        typeof s.index === "number"
                          ? rows[s.index]?.find(
                              (item) => item.index === heading?.value
                            )?.value || "unknown"
                          : s.email || "unknown",
                    }))
                  );

                  setEmailHeading(heading?.value || 0);
                }}
                options={headings.map((heading) => ({
                  label: heading.value,
                  value: heading.index,
                }))}
                noOptionsMessage={() => "No Options"}
                className="w-44"
                styles={{
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 20,
                  }),
                }}
              />
            </div>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center justify-center">
          {students.map(({ id, name, email }) => (
            <div
              key={id}
              className="relative mx-2 my-5 rounded bg-gray-100 p-2 shadow"
              style={{ maxWidth: "300px" }}
            >
              {students.length > 1 ? (
                <span className="absolute right-0 top-0 -translate-y-3 translate-x-3 transform">
                  <button
                    onClick={() =>
                      setStudents(students.filter((t) => t.id != id))
                    }
                  >
                    <XCircleIcon className="w-6 transform text-gray-700 transition hover:scale-125" />
                  </button>
                </span>
              ) : null}
              <label htmlFor={"name" + id}>
                Name
                <input
                  type="text"
                  name="name"
                  id={"name" + id}
                  value={name}
                  onChange={(e) => {
                    setStudents(
                      students.map((s) => {
                        if (s.id != id) return s;

                        return {
                          ...s,
                          name: e.target.value,
                        };
                      })
                    );
                  }}
                  className="mb-6 mt-2 w-full rounded border border-solid border-gray-400 p-2 shadow"
                />
              </label>
              <label htmlFor={"email" + id}>
                Email
                <input
                  type="email"
                  name="email"
                  id={"email" + id}
                  value={email}
                  onChange={(e) => {
                    setStudents(
                      students.map((s) => {
                        if (s.id != id) return s;

                        return {
                          ...s,
                          email: e.target.value,
                        };
                      })
                    );
                  }}
                  className="mb-6 mt-2 w-full rounded border border-solid border-gray-400 p-2 shadow"
                />
              </label>
            </div>
          ))}
          <div
            onClick={() =>
              setStudents([
                ...students,
                {
                  id: nanoid(),
                  name: "",
                  email: "",
                },
              ])
            }
            className="group mx-2 my-5 flex cursor-pointer items-center justify-center p-2"
            style={{ width: "300px" }}
          >
            <Tooltip content="Add a Student">
              <span>
                <PlusIcon className="w-18 transform text-gray-300 transition group-hover:scale-150" />
              </span>
            </Tooltip>
          </div>
        </div>

        {hasCSV ? null : (
          <>
            {/* OR */}
            <div className="mx-auto flex max-w-2xl items-center justify-center gap-2.5 py-10">
              <div className="h-0 w-32 border border-solid border-gray-500"></div>
              <span className="text-xl">OR</span>
              <div className="h-0 w-32 border border-solid border-gray-500"></div>
            </div>

            {/* Invite via pasting text to be ingested and parsed */}
            <div className="flex items-center justify-center p-2">
              {isParsing ? (
                <div className="flex flex-col items-center justify-center gap-4 p-16">
                  <div className="spinner h-20 w-20" />
                  <p className="font-normal text-cyan-600">Abracadabra...</p>
                </div>
              ) : (
                <>
                  <TextEditor
                    content={ingestText}
                    onUpdate={(content) => setIngestText(content)}
                    compact={true}
                  />
                  {ingestText ? (
                    <Tooltip content="Send to ClassCart AI">
                      <span>
                        <button
                          className="group/button flex h-16 w-16 items-center justify-center rounded-full bg-green-300 shadow-inner hover:scale-105"
                          onClick={async () => {
                            setIsParsing(true);

                            const response = (await toast
                              .promise(
                                fetch("/api/ai-text-to-users", {
                                  method: "POST",
                                  headers: {
                                    "Content-Type": "application/json",
                                  },
                                  body: JSON.stringify({
                                    blob: ingestText,
                                  }),
                                }),
                                {
                                  loading: "Parsing user information...",
                                  success:
                                    "User information parsed successfully.",
                                  error: "Failed to parse user information.",
                                }
                              )
                              .then((response) => {
                                setIsParsing(false);

                                return response.json();
                              })) as ResponseData;

                            if (response.error) {
                              console.error(response.error);
                              toast.error("Failed to parse user information.");
                            }

                            if (response.users) {
                              console.log(response.users);

                              const tempStudents = response.users
                                .filter((user) =>
                                  user.roles.some((role) => role === "student")
                                )
                                .map((user) => ({
                                  id: nanoid(),
                                  name: user.name,
                                  email: user.email,
                                }));

                              // load students into the state, not replacing existing students
                              setStudents((prevStudents) => {
                                const existingEmails = new Set(
                                  prevStudents.map((t) => t.email.toLowerCase())
                                );
                                const newStudents = tempStudents.filter(
                                  (t) =>
                                    !existingEmails.has(t.email.toLowerCase())
                                );
                                return [
                                  ...prevStudents.filter((t) => t.email),
                                  ...newStudents,
                                ];
                              });
                            }
                          }}
                          disabled={isParsing}
                        >
                          <PaperAirplaneIcon
                            className={classNames(
                              "icon group-hover/button:scale-150",
                              isParsing ? "animate-wiggle" : ""
                            )}
                          />
                        </button>
                      </span>
                    </Tooltip>
                  ) : null}
                </>
              )}
            </div>

            {/* OR */}
            <div className="mx-auto flex max-w-2xl items-center justify-center gap-2.5 py-10">
              <div className="h-0 w-32 border border-solid border-gray-500"></div>
              <span className="text-xl">OR</span>
              <div className="h-0 w-32 border border-solid border-gray-500"></div>
            </div>

            {/* Invite via CSV file */}
            <h2 className="mb-5 text-center font-normal">
              Invite From CSV File
            </h2>
            <div className="mx-auto w-min">
              <Dropzone
                accept={[
                  "text/comma-separated-values",
                  "text/csv",
                  "text/anytext",
                  "application/csv",
                  "application/excel",
                  "application/vnd.msexce",
                  "application/vnd.ms-excel",
                ]}
                multiple={false}
                onDrop={(acceptedFiles, rejectedFiles) => {
                  console.log("Added files", acceptedFiles);
                  console.log("Rejected files", rejectedFiles);

                  if (
                    !acceptedFiles.filter((file) => file.name.endsWith(".csv"))
                      .length
                  ) {
                    toast.error("You can only upload .csv files");
                    return;
                  }

                  const file = acceptedFiles.filter((file) =>
                    file.name.endsWith(".csv")
                  )[0];
                  const reader = new FileReader();
                  reader.onload = async (event) => {
                    const result = (event.target?.result ?? "") as string;

                    const resultArray = result.split("\n");
                    console.log("resultArray", resultArray);

                    const [headingsString, ...rowStrings] = resultArray;
                    console.log("headingsString", headingsString);
                    console.log("rowStrings", rowStrings);
                    const untangleRow = (rowString: string) =>
                      rowString
                        .split(",")
                        .map((row) => row.match(/"?.+"?/)?.[0])
                        .filter((row) => row)
                        .map((row: string) => row.replace(/"/g, ""))
                        .map((row, index) => ({ index, value: row }));

                    const headings = untangleRow(headingsString);
                    const rows = rowStrings
                      .map((rowString) => untangleRow(rowString))
                      .filter((row) => row.length === headings.length);

                    setHeadings(headings);
                    setRows(rows);
                    console.log("headings", headings);
                    console.log("rows", rows);

                    // Determine best options for name and email
                    const emailSimilarities = headings.map(
                      ({ index, value }) => ({
                        index,
                        similarity: compareTwoStrings("email", value),
                      })
                    );
                    const emailHeading = emailSimilarities.reduce(
                      (prev, curr) =>
                        prev.similarity > curr.similarity ? prev : curr
                    );

                    const nameSimilarities = headings.map(
                      ({ index, value }) => ({
                        index,
                        similarity: compareTwoStrings("name", value),
                      })
                    );
                    const nameHeading = nameSimilarities.reduce((prev, curr) =>
                      prev.index === emailHeading.index
                        ? curr
                        : prev.similarity > curr.similarity
                        ? prev
                        : curr
                    );

                    setEmailHeading(emailHeading.index);
                    setNameHeading(nameHeading.index);

                    setStudents(
                      rows.map((row, index) => ({
                        id: nanoid(),
                        name:
                          row.find((item) => item.index === nameHeading.index)
                            ?.value || "unknown",
                        email:
                          row.find((item) => item.index === emailHeading.index)
                            ?.value || "unknown",
                        index,
                      }))
                    );
                  };

                  reader.readAsText(file);
                }}
              >
                {({
                  getRootProps,
                  getInputProps,
                  isDragAccept,
                  isDragReject,
                  isDragActive,
                }) => (
                  <section>
                    <DropzoneContainer
                      {...getRootProps({
                        isDragAccept,
                        isDragReject,
                        isDragActive,
                      })}
                    >
                      <input {...getInputProps()} />
                      <h3>Drop files here, or click to select files</h3>
                      <p>Supports .csv files</p>
                    </DropzoneContainer>
                  </section>
                )}
              </Dropzone>
            </div>
          </>
        )}

        <Error error={error} />
        <button className="btn btn-warning" onClick={close}>
          Cancel
        </button>
        <UserContext.Consumer>
          {(user) => (
            <button
              className="btn btn-primary"
              onClick={() =>
                updateSchool({
                  where: { id: user.school?.id },
                  data: {
                    users: {
                      upsert: students.map(({ name, email }) => ({
                        where: { email: email.toLowerCase() },
                        update: {
                          name: { set: name },
                          email: { set: email.toLowerCase() },
                          password: { set: nanoid() },
                          roles: { connect: [{ name: "student" }] },
                        },
                        create: {
                          name,
                          email: email.toLowerCase(),
                          password: nanoid(),
                          roles: { connect: [{ name: "student" }] },
                        },
                      })),
                    },
                  },
                }).then(() => {
                  mixpanel.track("Invited students", { csv: hasCSV });

                  setStudents([
                    {
                      id: nanoid(),
                      name: "",
                      email: "",
                    },
                  ]);

                  close();
                })
              }
              disabled={
                fetching ||
                !students.length ||
                students.some((s) => !s.name || !s.email)
              }
            >
              {fetching ? <div className="spinner"></div> : null}
              Invite Students
            </button>
          )}
        </UserContext.Consumer>
      </DialogComponent>
    </>
  );
};

interface RemoveStudentFromClassProps {
  user: CurrentUser;
  student: NonNullable<CurrentUser["school"]>["users"][number];
  c: NonNullable<CurrentUser["school"]>["users"][number]["classes"][number];
}

const RemoveStudentFromClass: React.FC<RemoveStudentFromClassProps> = ({
  user,
  student,
  c,
}) => {
  const [{ fetching, error }, updateSchool] = useUpdateSchoolMutation();

  return (
    <Tooltip content={`Remove ${student.name} From ${c.name}`}>
      <button
        onClick={async () => {
          console.log("YOLO");

          await updateSchool({
            where: { id: user.school?.id },
            data: {
              classes: {
                update: [
                  {
                    where: { id: c.id },
                    data: {
                      users: {
                        disconnect: [{ id: student.id }],
                      },
                    },
                  },
                ],
              },
            },
          });
        }}
        disabled={fetching}
      >
        {fetching ? (
          <div className="spinner" />
        ) : (
          <XMarkIcon className="icon icon-gray opacity-0 group-hover:opacity-100" />
        )}
      </button>
    </Tooltip>
  );
};

export default SettingsStudents;
